import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout';
import { UnifiedTable } from '@/components/business/UnifiedTable';
import {
  loadStats,
  getTopPlayersWithDecay,
  loadEloHistoryRaw,
} from '@/utils/dataLoader';
import type { Player, Team, HistoricalPlayer } from '@/types';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [players, setPlayers] = useState<Player[]>([]);
  const [stats, setStats] = useState<{
    totalPlayers?: number;
    averageElo?: number;
    lastUpdated?: string;
  }>({});
  const [dataEndDate, setDataEndDate] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [playersData, statsData, eloHistoryData] = await Promise.all([
          getTopPlayersWithDecay(500), // 显示前几名（带衰减）
          loadStats(),
          loadEloHistoryRaw(),
        ]);
        setPlayers(playersData);
        setStats(statsData);
        setDataEndDate(eloHistoryData.meta?.endDate || '');
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('加载数据失败:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handlePlayerClick = (record: Player | Team | HistoricalPlayer) => {
    const player = record as Player;
    navigate(`/player/${player.id}`); // 使用数据库主键ID
  };

  if (loading) {
    return (
      <Layout currentPath='/'>
        <div className='flex justify-center items-center h-64'>
          <div className='text-lg text-text-secondary'>加载中...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout currentPath='/'>
      <div className='space-y-8'>
        {/* 页面标题 */}
        <div className='text-center'>
          <h1 className='text-3xl font-bold text-text-primary mb-2'>
            王者荣耀职业选手排名系统
          </h1>
          <p className='text-text-secondary'>基于ELO算法的专业电竞数据平台</p>
          <p className='text-text-primary'>
            网站仍在开发中，存在大量疏漏和临时内容……
          </p>
        </div>

        {/* 统计信息 */}
        <div className='bg-background-secondary rounded-xl p-6 border border-border'>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-6 text-center'>
            <div className='space-y-1'>
              <div className='text-2xl font-bold text-text-primary'>
                {dataEndDate || 'N/A'}
              </div>
              <div className='text-sm text-text-secondary'>最后更新日期</div>
            </div>
            <div className='space-y-1'>
              <div className='text-2xl font-bold text-text-primary'>
                {stats.averageElo || 'N/A'}
              </div>
              <div className='text-sm text-text-secondary'>平均ELO</div>
            </div>
            <div className='space-y-1'>
              <div className='text-2xl font-bold text-text-primary'>
                {stats.totalPlayers || players.length}
              </div>
              <div className='text-sm text-text-secondary'>选手数量</div>
            </div>
          </div>
        </div>

        {/* 选手排名表格 */}
        <div className='space-y-4'>
          <h2 className='text-xl font-semibold text-text-primary flex items-center gap-2'>
            <span className='w-1 h-6 bg-glory-gold rounded'></span>
            选手ELO排名 Top {players.length}
          </h2>
          <UnifiedTable
            variant='player'
            data={players}
            onRowClick={handlePlayerClick}
            teamNameDate={dataEndDate}
          />
        </div>
      </div>
    </Layout>
  );
};

export default HomePage;
